import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import Grid from "@material-ui/core/Grid";
import { compose } from "ramda";
import { RouteComponentProps } from "react-router-dom";
import axios from "axios";
import queryString from "query-string";
import {
  PaymentTypeCard,
  Typography,
  showToast,
  Button,
} from "@deeptrue-frontend/ui";
import { OrganizationType } from "store/modules/auth/auth";
import { Dashboard } from "components/Layout";
import api from "api/api";
import StoreProvider from "components/StoreProvider/StoreProvider";
import RouterProvider from "components/RouterProvider/RouterProvider";
import { useFormatMessage } from "components/IntlProvider";
import Invoice from "./Invoice";

import HistoryTable from "./HistoryTable";
import TagManager from "react-gtm-module";

const { Link, ROUTES } = RouterProvider;
const { connect, selectors } = StoreProvider;
const ONE_SECOND = 1000;

const StyledRootContainer = styled.section`
  position: relative;
  margin-top: 2rem;
  flex-grow: 1;
`;

const StyledCardBlock = styled(Grid)`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
`;

const StyledOrderHistoryBlock = styled.div`
  margin-top: 3rem;
  margin-bottom: 1rem;
  overflow-x: auto;
`;

const StyledOrderHistoryTopRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 1.5rem 0;
`;

const StyledLimitsBlock = styled.div`
  position: absolute;
  right: 1rem;
  top: -95px;
  display: flex;
`;
const StyledLimitsItem = styled.div`
  padding: 0.8rem 1.5rem;
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.white};
`;

interface PaymentsProps extends RouteComponentProps<any> {
  organization: OrganizationType;
}

interface Invoice {
  id: number;
  invoice_pdf: string;
}

interface PaymentsResult {
  id: number;
  created_at: string;
  invoice: Invoice;
  stripe_session_id: string;
  paid: boolean;
  email: string;
  address_info: number;
  quantity: number;
  amount: number;
  currency: string;
  type: number;
}

interface PaymentsData {
  count: number;
  next: string | null;
  previous: string | null;
  results: Array<PaymentsResult>;
}
interface LimitData {
  limit: number;
  available_limit: number;
  reserved_tokens: number;
  available_tokens: number;
}

const Payments = ({ organization, location, history }: PaymentsProps) => {
  const [paymentsRes, setPaymentsRes] = useState<PaymentsData>({
    count: -1,
    next: null,
    previous: null,
    results: [],
  });

  const [limitsRes, setLimitsRes] = useState<LimitData>({
    limit: 0,
    available_limit: 0,
    reserved_tokens: 0,
    available_tokens: 0,
  });
  const parsedSearch = queryString.parse(location.search);
  const t = useFormatMessage();

  useEffect(() => {
    const { payment, type } = queryString.parse(location.search);

    if (payment && payment === "success") {
      if (type !== "invoice") {
        // Credit Card payment
        TagManager.dataLayer({
          dataLayer: {
            event: "credits_bought",
            date: new Date().toLocaleString(),
            ...organization,
          },
        });
      }

      showToast({
        type: "success",
        title: t("success.toast.payment", "Payment success"),
      });
      if ("projectRedirectionAfterPayment" in localStorage) {
        const route = localStorage.projectRedirectionAfterPayment;
        localStorage.removeItem("projectRedirectionAfterPayment");
        history.push(route);
      } else {
        setTimeout(() => {
          history.replace(ROUTES.PAYMENTS.MAIN);
        }, ONE_SECOND);
      }
    }
    axios
      .all([
        api.resources.getOrganizationPayments(
          organization.codename,
          parsedSearch as {
            limit: string;
            offset: string;
            search: string;
          }
        ),
        api.resources.getOrganizationPaymentLimits(organization.codename),
      ])
      .then(
        axios.spread((payments, limits) => {
          setPaymentsRes(payments.data);
          setLimitsRes(limits.data);
        })
      );
    // eslint-disable-next-line
  }, [organization.codename]);

  return (
    <Dashboard
      title={t("payments.title", "Payments")}
      location={location}
      headerTitle={t("payments.headerTitle", "Payments Dashboard")}
      headerSubtitle={t("payments.headerSubtitle", "Select payment method")}
    >
      <StyledRootContainer>
        <StyledLimitsBlock>
          <StyledLimitsItem style={{ marginRight: "1rem" }}>
            <Typography variant="eyebrow2">
              {t("pages.payment.balance", "Current Balance")}
            </Typography>
            <Typography variant="h5" {...{ color: "#17A2B8" }}>
              {limitsRes.available_tokens}
            </Typography>
          </StyledLimitsItem>
          <StyledLimitsItem>
            <Typography variant="eyebrow2">
              {t("pages.payment.reservedcredits", "Reserved Credits")}
            </Typography>
            <Typography variant="h5" {...{ color: "#B5B5B0" }}>
              {limitsRes.reserved_tokens}
            </Typography>
          </StyledLimitsItem>
        </StyledLimitsBlock>
        <StyledCardBlock container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Link exact to={ROUTES.PAYMENTS.CREDIT_CARD_INFO}>
              <PaymentTypeCard
                title={t("pages.payment.cardpayment", "Card Payment")}
                subtitle={t(
                  "pages.payment.cardpaymenttext",
                  "Pay securely with a credit card"
                )}
                type={1}
              />
            </Link>
          </Grid>
          {organization.trusted && (
            <Grid
              item
              xs={12}
              sm={6}
              style={
                limitsRes.available_limit === 0
                  ? {
                      opacity: "0.5",
                    }
                  : {}
              }
            >
              <Link
                exact
                to={
                  organization.trusted
                    ? ROUTES.PAYMENTS.INVOICE_INFO
                    : ROUTES.PAYMENTS.MAIN
                }
              >
                <PaymentTypeCard
                  title={t("pages.payment.invoicepayment", "Invoice Payment")}
                  subtitle={
                    t("pages.payment.pendinglimit", "Pending Limit: ") +
                    limitsRes.available_limit +
                    "/" +
                    limitsRes.limit
                  }
                  type={2}
                />
              </Link>
            </Grid>
          )}
        </StyledCardBlock>

        {paymentsRes.count > 0 && (
          <StyledOrderHistoryBlock>
            <StyledOrderHistoryTopRow>
              <Typography variant="h6">
                {t("pages.payment.tabletitle", "Order history")}
              </Typography>

              <Link exact to={ROUTES.PAYMENTS.ORDER_HISTORY}>
                <Button variant="outlined">
                  {t("pages.payment.fullhistory", "Full history")}
                </Button>
              </Link>
            </StyledOrderHistoryTopRow>
            <HistoryTable
              payments={paymentsRes.results}
              count={paymentsRes.count}
              currentPage={0}
            />
          </StyledOrderHistoryBlock>
        )}
      </StyledRootContainer>
    </Dashboard>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
  }))
)(Payments);
